import api from './api';

/**
 * Calling a patient - a Twilio phone conference, or a WhatsApp call placed from
 * the browser. Both flows join the same conference on the backend; the difference
 * is whether the therapist's leg is their phone or this tab.
 */

export type CallType = 'both' | 'patient' | 'user';

export type CallPermissionStatus = 'accepted' | 'rejected' | 'expired' | 'requested' | 'unknown';

export interface VoiceToken {
  token: string;
  identity: string;
  ttl: number;
}

export interface CallPatientResult {
  success: boolean;
  conference_name: string;
  patient_call_sid: string | null;
  therapist_call_sid: string | null;
}

export interface CallPermission {
  status: CallPermissionStatus;
  phone: string | null;
  updated_at: string | null;
  valid_until: string | null;
  requested_at: string | null;
}

export interface CallPermissionResult {
  success?: boolean;
  already_approved?: boolean;
  already_sent?: boolean;
  sid?: string;
  error?: string;
}

export const voiceService = {
  /** Dial the patient and the therapist into a Twilio conference. */
  async callPatient(payload: {
    patient_id: number;
    meeting_id?: number;
    phone?: string;
    call_type?: CallType;
  }): Promise<CallPatientResult> {
    const response = await api.post('/voice/call-patient', { call_type: 'both', ...payload });
    return response.data;
  },

  /** Access token for the browser's Twilio Voice device. */
  async getVoiceToken(): Promise<VoiceToken> {
    const response = await api.post('/voice/token');
    return response.data;
  },

  /** Cancel a WhatsApp leg that is still ringing after the therapist hung up. */
  async cancelWhatsAppCall(payload: { patient_id: number; phone?: string }): Promise<{ canceled: number }> {
    const response = await api.post('/voice/whatsapp/cancel', payload);
    return response.data;
  },

  async getWhatsAppCallPermission(payload: { patient_id: number; phone?: string }): Promise<CallPermission> {
    const response = await api.post('/voice/whatsapp/permission-status', payload);
    return response.data;
  },

  async requestWhatsAppCallPermission(payload: { patient_id: number; phone?: string }): Promise<CallPermissionResult> {
    const response = await api.post('/voice/whatsapp/permission', payload);
    return response.data;
  },
};

export default voiceService;
